import React, { useEffect, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { Link } from "react-router-dom";
import { STAGES, SELLER_STAGES, stageLabel, PRIORITIES, priorityCls, SOURCE_LABELS, KIND_LABELS, fmtDate } from "./shared";
import MatchProfile from "./MatchProfile";
import LeadPortalSection from "./LeadPortalSection";
import { X, UserCheck, Trash2, Send, MapPin, Eye, Calendar, Paperclip, Download, MessageCircle } from "lucide-react";

export default function LeadDrawer({ leadId, agents, onClose, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [note, setNote] = useState("");
    const [tagInput, setTagInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [nameVal, setNameVal] = useState("");

    const load = async () => {
        try {
            const r = await api.get(`/crm/leads/${leadId}`);
            setDetail(r.data);
            setNameVal(r.data.lead.name || "");
            const a = await api.get(`/crm/lead-attachments/${leadId}`).catch(() => ({ data: { items: [] } }));
            setAttachments(a.data.items || []);
        } catch { onClose(); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [leadId]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const update = async (patch) => {
        try {
            const r = await api.put(`/crm/leads/${leadId}`, patch);
            setDetail((d) => ({ ...d, lead: r.data }));
            onChanged();
        } catch (e) {
            if (e?.response?.status === 403) window.alert(e.response.data?.detail || "Permesso negato");
        }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        setBusy(true);
        try { await api.post(`/crm/leads/${leadId}/notes`, { text: note }); setNote(""); await load(); onChanged(); }
        finally { setBusy(false); }
    };

    const convert = async () => {
        await api.post(`/crm/leads/${leadId}/convert`);
        await load(); onChanged();
    };

    const remove = async () => {
        if (!window.confirm("Eliminare questo lead e la sua cronologia?")) return;
        await api.delete(`/crm/leads/${leadId}`);
        onChanged(); onClose();
    };

    const addTag = () => {
        const t = tagInput.trim();
        if (!t || !detail) return;
        update({ tags: [...(detail.lead.tags || []), t] });
        setTagInput("");
    };

    if (!detail) return null;
    const l = detail.lead;

    return (
        <div className="fixed inset-0 z-[55]">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div data-testid="lead-drawer" className="absolute right-0 top-0 h-full w-full max-w-lg bg-ink-950 border-l border-white/15 overflow-y-auto">
                <div className="sticky top-0 bg-ink-950/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <input data-testid="lead-name-edit" value={nameVal} onChange={(e) => setNameVal(e.target.value)}
                            onBlur={() => { const v = nameVal.trim(); if (v && v !== l.name) update({ name: v }); else setNameVal(l.name || ""); }}
                            className="font-serif text-xl text-ink-50 bg-transparent border-b border-transparent hover:border-white/20 focus:border-gold outline-none min-w-0 flex-1" />
                        {l.type === "client"
                            ? <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-green-900/30 text-green-300 border border-green-700/40 shrink-0">Cliente</span>
                            : l.kind === "seller"
                                ? <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-purple-900/30 text-purple-300 border border-purple-700/40 shrink-0">Venditore</span>
                                : <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-900/30 text-blue-300 border border-blue-700/40 shrink-0">Lead</span>}
                    </div>
                    <button data-testid="drawer-close" onClick={onClose} className="text-ink-400 hover:text-ink-50"><X size={18} /></button>
                </div>

                <div className="px-6 py-5 space-y-6">
                    {/* Contact info */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <EditField testid="lead-email" label="Email" value={l.email} onSave={(v) => update({ email: v })} />
                        <EditField testid="lead-phone" label="Telefono" value={l.phone} onSave={(v) => update({ phone: v })} />
                    </div>
                    {l.phone && (
                        <button data-testid="lead-whatsapp" onClick={async () => {
                            let digits = (l.phone || "").replace(/\D/g, "");
                            if (digits.length === 10 && digits.startsWith("3")) digits = "39" + digits;
                            const msg = encodeURIComponent(`Buongiorno ${l.name}, la contatto in merito ${l.property_title ? `all'immobile "${l.property_title}"` : "alla sua richiesta"}. Quando possiamo sentirci?`);
                            window.open(`https://wa.me/${digits}?text=${msg}`, "_blank");
                            try { await api.post(`/crm/leads/${leadId}/log-whatsapp`); await load(); onChanged(); } catch (e) { console.error(e); }
                        }} className="w-full py-2.5 text-xs flex items-center justify-center gap-2 border border-green-600/50 text-green-300 hover:bg-green-900/20 transition-colors">
                            <MessageCircle size={14} /> Scrivi su WhatsApp
                        </button>
                    )}
                    <div className="text-[11px] text-ink-400">Origine: <span className="text-ink-100">{SOURCE_LABELS[l.source] || l.source}</span> · Creato {fmtDate(l.created_at)}</div>

                    {/* Stage / priority / agent */}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Fase</label>
                            <select data-testid="lead-stage" value={l.stage} onChange={(e) => update({ stage: e.target.value })}
                                className="w-full bg-ink-950 border border-white/15 px-2 py-2 text-xs text-ink-50 outline-none">
                                {(l.kind === "seller" ? SELLER_STAGES : STAGES).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Priorità</label>
                            <select data-testid="lead-priority" value={l.priority} onChange={(e) => update({ priority: e.target.value })}
                                className="w-full bg-ink-950 border border-white/15 px-2 py-2 text-xs text-ink-50 outline-none">
                                {PRIORITIES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Agente</label>
                            <select data-testid="lead-agent" value={l.agent || ""} onChange={(e) => update({ agent: e.target.value || null })}
                                className="w-full bg-ink-950 border border-white/15 px-2 py-2 text-xs text-ink-50 outline-none">
                                <option value="">—</option>
                                {agents.map((a) => <option key={a} value={a}>{a}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Tags */}
                    <div>
                        <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-2">Tag</label>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {(l.tags || []).map((t, i) => (
                                <span key={`${t}-${i}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border border-purple-700/40 bg-purple-900/20 text-purple-200">
                                    {t}<button onClick={() => update({ tags: l.tags.filter((_, j) => j !== i) })} className="hover:text-red-300"><X size={10} /></button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input data-testid="lead-tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder="Aggiungi tag..."
                                className="flex-1 bg-transparent border border-white/15 px-2 py-1.5 text-xs text-ink-50 focus:border-gold outline-none" />
                            <button data-testid="lead-tag-add" onClick={addTag} className="cta-outline px-3 text-[11px]">+</button>
                        </div>
                    </div>

                    {/* Profilo ricerca / dati venditore + matching */}
                    <MatchProfile lead={l} onSaved={load} />

                    {/* Portale cliente: link, proposte immobili, feedback */}
                    <LeadPortalSection lead={l} onChanged={() => { load(); onChanged(); }} />

                    {/* Linked property */}
                    {detail.property && (
                        <div className="border border-white/10 p-4 bg-ink-900/40">
                            <div className="text-[10px] text-ink-400 uppercase tracking-wider mb-2">Immobile collegato</div>
                            <Link to={`/annunci/${detail.property.id}`} className="font-serif text-ink-50 hover:text-gold">{detail.property.title}</Link>
                            <div className="flex items-center gap-4 mt-2 text-[11px] text-ink-400">
                                <span className="flex items-center gap-1"><MapPin size={11} />{detail.property.city}</span>
                                <span className="text-price">{formatPrice(detail.property.price)}</span>
                                <span className="flex items-center gap-1"><Eye size={11} />{detail.property.views || 0} views</span>
                                <span>{detail.property.status}</span>
                            </div>
                        </div>
                    )}

                    {/* Appointments */}
                    {(detail.appointments || []).length > 0 && (
                        <div>
                            <div className="text-[10px] text-ink-400 uppercase tracking-wider mb-2">Appuntamenti</div>
                            {detail.appointments.map((a) => (
                                <div key={a.id} className="flex items-center gap-2 text-xs text-ink-100 py-1.5 border-b border-white/5">
                                    <Calendar size={12} className="text-purple-300" />
                                    <span className="flex-1 truncate">{a.property_title}</span>
                                    <span className="text-ink-400">{fmtDate(a.start_at)}</span>
                                    <span className={`text-[9px] uppercase px-1.5 py-0.5 ${a.status === "confirmed" ? "bg-green-900/30 text-green-300" : a.status === "pending" ? "bg-amber-900/30 text-amber-300" : "bg-ink-900 text-ink-400"}`}>{a.status}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Attachments */}
                    {attachments.length > 0 && (
                        <div>
                            <div className="text-[10px] text-ink-400 uppercase tracking-wider mb-2">Allegati email</div>
                            {attachments.map((a) => (
                                <div key={a.id} className="flex items-center gap-2 text-xs text-ink-100 py-1.5 border-b border-white/5">
                                    <Paperclip size={12} className="text-gold shrink-0" />
                                    <span className="flex-1 truncate">{a.filename}</span>
                                    <span className="text-ink-500 text-[10px]">{a.size >= 1048576 ? `${(a.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(a.size / 1024))} KB`}</span>
                                    <button data-testid={`lead-att-${a.id}`} onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/crm/lead-attachments/${a.id}/download`, "_blank")}
                                        className="text-ink-400 hover:text-gold"><Download size={12} /></button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                        {l.type !== "client" && (
                            <button data-testid="lead-convert" onClick={convert} className="flex-1 cta-gold py-2.5 text-xs flex items-center justify-center gap-2">
                                <UserCheck size={13} /> Converti in Cliente
                            </button>
                        )}
                        <button data-testid="lead-delete" onClick={remove} className="px-3 py-2.5 border border-red-700/40 text-red-300 hover:bg-red-900/20 text-xs flex items-center gap-1.5">
                            <Trash2 size={13} /> Elimina
                        </button>
                    </div>

                    {/* Notes */}
                    <div>
                        <div className="text-[10px] text-ink-400 uppercase tracking-wider mb-2">Aggiungi nota</div>
                        <div className="flex gap-2">
                            <input data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Scrivi una nota..."
                                className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none" />
                            <button data-testid="lead-note-add" onClick={addNote} disabled={busy || !note.trim()} className="cta-outline px-3 disabled:opacity-40"><Send size={13} /></button>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div>
                        <div className="text-[10px] text-ink-400 uppercase tracking-wider mb-3">Timeline</div>
                        <div className="space-y-0 border-l border-white/10 pl-4" data-testid="lead-timeline">
                            {(detail.activities || []).map((a) => (
                                <div key={a.id} className="relative pb-4">
                                    <div className={`absolute -left-[21px] top-1 w-2 h-2 rounded-full ${a.kind === "nota" ? "bg-gold" : a.kind === "stage" ? "bg-purple-400" : a.kind === "conversione" ? "bg-green-400" : "bg-ink-500"}`} />
                                    <div className="text-[10px] text-ink-400 uppercase tracking-wider">{KIND_LABELS[a.kind] || a.kind} · {fmtDate(a.created_at)}</div>
                                    <div className="text-sm text-ink-100 mt-0.5">{a.text}</div>
                                </div>
                            ))}
                            {(detail.activities || []).length === 0 && <div className="text-xs text-ink-500 pb-2">Nessuna attività registrata.</div>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function EditField({ label, value, onSave, testid }) {
    const [v, setV] = useState(value || "");
    useEffect(() => { setV(value || ""); }, [value]);
    return (
        <div>
            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">{label}</label>
            <input data-testid={testid} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== (value || "") && onSave(v)}
                className="w-full bg-transparent border border-white/15 px-2.5 py-1.5 text-sm text-ink-50 focus:border-gold outline-none" />
        </div>
    );
}
